import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { AlertTriangle, BookOpen, CheckCircle2, Clock, Lock, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor } from "@am/shared";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { PageHeader } from "../components/PageHeader";
import { ConfirmModal } from "../components/ConfirmModal";

interface ConfirmConfig {
  title: React.ReactNode;
  body: React.ReactNode;
  okLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
}

interface WcSync {
  status: "running" | "ok" | "failed" | null;
  last_sync_at: string | null;
  result: { products_synced?: number; chunks_upserted?: number; error?: string } | null;
}

interface KbStats {
  total: number;
  embedded: number;
  pending: number;
  outdated: number;
  failed: number;
  wc_configured: boolean;
  wc_sync: WcSync;
}

interface KbDoc {
  id: string;
  title: string;
  content: string;
  doc_type: string;
  embed_status: "embedded" | "pending" | "outdated" | "failed";
  embed_error?: string | null;
  chunk_count: number;
  updated_at?: string;
  is_locked?: boolean;
}

const DOC_TYPES = ["info", "faq", "service", "policy", "custom"];

const STATUS_ICON: Record<KbDoc["embed_status"], JSX.Element> = {
  embedded: <CheckCircle2 size={12} />,
  pending: <Clock size={12} />,
  outdated: <RefreshCw size={12} />,
  failed: <XCircle size={12} />,
};

export function KbPage() {
  const api = useApiClient();
  const { t } = useI18n();

  const [stats, setStats] = useState<KbStats | null>(null);
  const [docs, setDocs] = useState<KbDoc[]>([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("");
  const [editing, setEditing] = useState<KbDoc | "new" | null>(null);
  const [wcSyncing, setWcSyncing] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmConfig | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const reload = useCallback(async () => {
    try {
      const [statsRes, docsRes] = await Promise.all([
        api<KbStats>("/tenant/api/kb"),
        api<{ docs: KbDoc[] }>("/tenant/api/kb/docs?limit=200"),
      ]);
      setStats(statsRes);
      setDocs(docsRes?.docs ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("error_generic"));
    }
  }, [api, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const lower = search.toLowerCase();
    return docs.filter((d) => {
      if (filterType && d.doc_type !== filterType) return false;
      if (lower && !d.title.toLowerCase().includes(lower)) return false;
      return true;
    });
  }, [docs, search, filterType]);

  async function wcSync() {
    setWcSyncing(true);
    try {
      await api("/tenant/api/kb/wc-sync", { method: "POST" });
      // Poll /kb every 3s until status leaves "running"
      pollRef.current = setInterval(async () => {
        try {
          const s = await api<KbStats>("/tenant/api/kb");
          setStats(s);
          if (s?.wc_sync?.status !== "running") {
            clearInterval(pollRef.current!);
            pollRef.current = null;
            setWcSyncing(false);
          }
        } catch {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setWcSyncing(false);
        }
      }, 3000);
    } catch (err) {
      toast.error("Erreur WooCommerce : " + (err instanceof Error ? err.message : "vérifiez les credentials"));
      setWcSyncing(false);
    }
  }

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  function onDelete(doc: KbDoc) {
    setConfirm({
      title: t("kb_delete_title"),
      body: (
        <>
          <strong>« {doc.title} »</strong> — {t("kb_delete_body")}
        </>
      ),
      okLabel: t("kb_delete_confirm"),
      danger: true,
      onConfirm: async () => {
        await api(`/tenant/api/kb/docs/${doc.id}`, { method: "DELETE" });
        setConfirm(null);
        toast.success(t("kb_delete_ok"));
        await reload();
      },
    });
  }

  async function handleEdit(doc: KbDoc) {
    try {
      const full = await api<KbDoc>(`/tenant/api/kb/docs/${doc.id}`);
      setEditing(full);
    } catch {
      toast.error("Impossible de charger le document");
    }
  }

  return (
    <div className="page is-active">
      <PageHeader titleKey="kb_title" subtitleKey="kb_subtitle" />

      <div className="kb-stats-row">
        <KbStatTile value={stats?.total ?? "—"} label={t("kb_stat_total")} />
        <KbStatTile value={stats?.embedded ?? "—"} label={t("kb_stat_embedded")} variant="is-ok" />
        <KbStatTile
          value={stats ? (stats.pending ?? 0) + (stats.outdated ?? 0) : "—"}
          label={t("kb_stat_pending")}
          variant="is-warn"
        />
        <KbStatTile value={stats?.failed ?? "—"} label={t("kb_stat_failed")} variant="is-err" />
      </div>

      {stats?.wc_configured && (
        <div className="kb-wc-banner" style={{ marginBottom: 20 }}>
          <RefreshCw size={18} className={wcSyncing ? "spin" : ""} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{t("kb_wc_title")}</div>
            <WcSyncStatus sync={stats.wc_sync} />
          </div>
          <button
            type="button"
            className="btn-ghost"
            style={{ whiteSpace: "nowrap" }}
            onClick={wcSync}
            disabled={wcSyncing}
          >
            {wcSyncing ? "Synchronisation…" : t("kb_wc_btn")}
          </button>
        </div>
      )}

      <div className="kb-toolbar">
        <input
          className="kb-search"
          type="text"
          placeholder={t("kb_search_ph")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterButton active={filterType === ""} onClick={() => setFilterType("")} label={t("kb_filter_all")} />
        <FilterButton active={filterType === "info"} onClick={() => setFilterType("info")} label="Info" />
        <FilterButton active={filterType === "faq"} onClick={() => setFilterType("faq")} label="FAQ" />
        <FilterButton active={filterType === "service"} onClick={() => setFilterType("service")} label="Service" />
        <FilterButton
          active={filterType === "policy"}
          onClick={() => setFilterType("policy")}
          label={t("kb_filter_policy")}
        />
        <FilterButton active={filterType === "custom"} onClick={() => setFilterType("custom")} label="Custom" />
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn-primary"
          style={{ width: "auto", padding: "9px 18px", fontSize: 13 }}
          onClick={() => setEditing("new")}
        >
          {t("kb_new_doc")}
        </button>
      </div>

      <div className="kb-doc-list">
        {filtered.length === 0 ? (
          <div className="kb-empty">
            <BookOpen size={36} />
            <div>{t("kb_empty")}</div>
          </div>
        ) : (
          filtered.map((doc) => (
            <KbDocRow
              key={doc.id}
              doc={doc}
              onEdit={() => handleEdit(doc)}
              onDelete={() => onDelete(doc)}
            />
          ))
        )}
      </div>

      {editing && (
        <KbEditorModal
          doc={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      <ConfirmModal
        open={confirm !== null}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.okLabel}
        cancelLabel={t("cancel")}
        danger={confirm?.danger}
        onConfirm={async () => {
          if (confirm) await confirm.onConfirm();
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function WcSyncStatus({ sync }: { sync: WcSync | undefined }) {
  if (!sync?.status) return <div style={{ fontSize: 12 }}>Aucune synchronisation effectuée.</div>;
  if (sync.status === "running") return <div style={{ fontSize: 12, color: "var(--yellow)" }}>Synchronisation en cours…</div>;
  if (sync.status === "failed") return (
    <div style={{ fontSize: 12, color: "var(--red)" }}>
      Échec — {sync.result?.error ?? "erreur inconnue"}
      {sync.last_sync_at && <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>{sync.last_sync_at.slice(0, 16).replace("T", " ")}</span>}
    </div>
  );
  return (
    <div style={{ fontSize: 12, color: "var(--green)" }}>
      {sync.result?.products_synced ?? 0} produits · {sync.result?.chunks_upserted ?? 0} chunks
      {sync.last_sync_at && <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>{sync.last_sync_at.slice(0, 16).replace("T", " ")}</span>}
    </div>
  );
}

function KbStatTile({ value, label, variant }: { value: number | string; label: string; variant?: string }) {
  return (
    <div className={"kb-stat" + (variant ? " " + variant : "")}>
      <div className="kb-stat-value">{value}</div>
      <div className="kb-stat-label">{label}</div>
    </div>
  );
}

function FilterButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={"kb-filter" + (active ? " is-active" : "")} onClick={onClick}>
      {label}
    </button>
  );
}

function KbDocRow({ doc, onEdit, onDelete }: { doc: KbDoc; onEdit: () => void; onDelete: () => void }) {
  const { t } = useI18n();
  const status = doc.embed_status;
  const statusLabel: Record<KbDoc["embed_status"], string> = {
    embedded: t("kb_status_embedded"),
    pending: t("kb_status_pending"),
    outdated: t("kb_status_outdated"),
    failed: t("kb_status_failed"),
  };
  const date = doc.updated_at ? doc.updated_at.slice(0, 10) : "";

  if (doc.is_locked) {
    return (
      <div className="kb-doc-row" style={{ opacity: 0.75 }}>
        <span className={`kb-doc-type-badge type-${doc.doc_type}`}>{doc.doc_type}</span>
        <div className="kb-doc-info">
          <div className="kb-doc-title" style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {doc.title}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "1px 7px 1px 5px",
                borderRadius: 5,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".05em",
                background: "rgba(139,92,246,.15)",
                color: "#9b8cff",
              }}
            >
              <Lock size={9} />
              {t("kb_locked_badge")}
            </span>
          </div>
          <div className="kb-doc-meta" style={{ fontStyle: "italic" }}>
            {t("kb_locked_content")} · {date}
          </div>
        </div>
        <span className={`kb-doc-status st-${status}`}>
          {STATUS_ICON[status]} {statusLabel[status]}
        </span>
        <div className="kb-doc-actions" />
      </div>
    );
  }

  return (
    <div className="kb-doc-row">
      <span className={`kb-doc-type-badge type-${doc.doc_type}`}>{doc.doc_type}</span>
      <div className="kb-doc-info">
        <div className="kb-doc-title">{doc.title}</div>
        <div className="kb-doc-meta">
          {doc.chunk_count} chunk{doc.chunk_count !== 1 ? "s" : ""} · {date}
        </div>
        {status === "failed" && doc.embed_error && (
          <div className="kb-doc-error" title={doc.embed_error} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <AlertTriangle size={12} /> {doc.embed_error.length > 80 ? doc.embed_error.slice(0, 80) + "…" : doc.embed_error}
          </div>
        )}
      </div>
      <span className={`kb-doc-status st-${status}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {STATUS_ICON[status]} {statusLabel[status]}
      </span>
      <div className="kb-doc-actions">
        <button type="button" className="kb-icon-btn" title="Modifier" onClick={onEdit}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        <button type="button" className="kb-icon-btn is-danger" title="Supprimer" onClick={onDelete}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KB editor modal — handles both create (doc=null) and edit (doc=KbDoc).
// ---------------------------------------------------------------------------

interface ModalProps {
  doc: KbDoc | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function KbEditorModal({ doc, onClose, onSaved }: ModalProps) {
  const api = useApiClient();
  const { t } = useI18n();
  const [title, setTitle] = useState(doc?.title ?? "");
  const [content, setContent] = useState(doc?.content ?? "");
  const [type, setType] = useState(doc?.doc_type ?? "info");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();
    if (!trimmedTitle) {
      setError("Le titre est obligatoire.");
      return;
    }
    if (!trimmedContent) {
      setError("Le contenu est obligatoire.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (doc) {
        await api(`/tenant/api/kb/docs/${doc.id}`, {
          method: "PUT",
          body: { title: trimmedTitle, content: trimmedContent, doc_type: type },
        });
      } else {
        await api("/tenant/api/kb/docs", {
          method: "POST",
          body: { title: trimmedTitle, content: trimmedContent, doc_type: type },
        });
      }
      toast.success(t("kb_doc_save_ok"));
      await onSaved();
    } catch (err) {
      setError("Erreur : " + (err instanceof Error ? err.message : "impossible d'enregistrer"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="kb-modal-overlay is-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="kb-modal">
        <div className="kb-modal-header">
          <div className="kb-modal-title">{doc ? t("kb_modal_edit") : t("kb_modal_new")}</div>
          <button type="button" className="kb-modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>{t("kb_label_title")}</label>
            <input
              type="text"
              maxLength={200}
              placeholder="Ex : Politique de retour, FAQ livraison..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field" style={{ marginBottom: 4 }}>
            <label>{t("kb_label_type")}</label>
            <div className="kb-type-selector">
              {DOC_TYPES.map((dt) => (
                <button
                  key={dt}
                  type="button"
                  className={"kb-type-btn" + (type === dt ? " is-selected" : "")}
                  onClick={() => setType(dt)}
                >
                  {dt === "policy" ? t("kb_filter_policy") : dt.charAt(0).toUpperCase() + dt.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="field" style={{ marginBottom: 4 }}>
            <label>{t("kb_label_content")}</label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Rédigez le contenu : titre, listes, tableau, lien…"
              minHeight={320}
            />
            <div className="kb-char-count">{content.length} / ~12 000 caractères max indexés</div>
          </div>
          {error && (
            <div style={{ fontSize: 12, color: "var(--red)", padding: "8px 0" }}>{error}</div>
          )}
          <div className="kb-modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              style={{ width: "auto", padding: "10px 24px" }}
              disabled={saving}
            >
              {saving ? "Enregistrement…" : doc ? t("kb_save") : t("kb_doc_create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
